import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";
import { QRCodeCanvas } from "qrcode.react";
import { fetchRouteRegistry } from "../Utils/fetchRouteRegistry";

const TABLET_TOKEN_API = `${API_BASE_URL}/seller/api/tablet-tokens`;

// The tablet is a SHARED device (vector 2026-07-06) — every driver on shift uses
// the same DRIVER token, the whole accounting team shares one ACCOUNTING token.
// Neither binds to a specific person/route, so there's no seller picker here.
// PACKER added 2026-09-23 (per vector): seller-module now owns every IMD role's
// token, so the IMD app's ขึ้นน้ำแข็ง (บรรจุ) role is issued from here too. Label
// says "ส่งเงิน" for DRIVER, not "คนขับ" — DRIVER already means ส่งเงิน (money-
// submit) in the running code, not the truck driver themselves.
//
// Vector 2026-09-23: "เราจะออก IMD คนบรรจุ กับ ส่งเงิน 2 อย่างเท่านั้น" — so this
// issue dropdown offers exactly PACKER + DRIVER. TRUCK_DRIVER (คนขับ) is frozen per
// "อย่าพึ่งทำอะไรกับคนขับ" and ACCOUNTING is not issued from here anymore. The
// ACCOUNTING role itself is left untouched on the backend (DriverReportService still
// honours it and any already-issued บัญชี token keeps working) — only new issuance
// from this IMD admin page is narrowed to the two field roles.
const ROLES = [
    { value: "PACKER", label: "บรรจุ" },
    { value: "DRIVER", label: "ส่งเงิน" },
    // SALES (2026-10-05): the sales app's card, bound to one route from the route registry (sales_route).
    { value: "SALES", label: "ขาย (ผูกสาย)" },
    // FRONT_COUNTER (2026-10-07): ขายหน้าโรง + คืนกระสอบ — โหมดแยกของตัวเอง ไม่ผูกสาย (Vector: "ให้เป็นโหมดแยกเลยนะ")
    { value: "FRONT_COUNTER", label: "ขายหน้าโรง" },
];

function getToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || "";
    } catch { return ""; }
}

function authHeaders() {
    return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
}

async function api(method, path, body) {
    const r = await fetch(`${TABLET_TOKEN_API}${path}`, {
        method,
        headers: authHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
    if (r.status === 204) return null;
    if (!r.ok) {
        const err = new Error(`${r.status} ${r.statusText}`);
        err.status = r.status;
        throw err;
    }
    return r.json();
}

export default function TabletTokenAdmin() {
    const [tokens, setTokens] = useState([]);
    const [label, setLabel] = useState("");
    const [role, setRole] = useState("");
    const [routeUid, setRouteUid] = useState("");
    const [routes, setRoutes] = useState([]); // route registry rows { uid, username, nameuser }
    const [newToken, setNewToken] = useState(null); // { id, label, role, token, createdAt }
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tokenSubTab, setTokenSubTab] = useState("Active");
    const [viewOpenId, setViewOpenId] = useState(null);
    const [viewData, setViewData] = useState({}); // id -> { loading, token, gone, error }

    const load = useCallback(async () => {
        setError("");
        try {
            setTokens(await api("GET", ""));
        } catch (e) {
            setError(e.message || "โหลดไม่สำเร็จ");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const { rows } = await fetchRouteRegistry(getToken());
                setRoutes(rows);
            } catch { setRoutes([]); }
        })();
    }, []);

    const routeName = (uid) => {
        const r = routes.find(x => x.uid === uid);
        return r ? `${r.nameuser}${r.username && r.username !== r.uid ? ` (${r.username})` : ""}` : uid;
    };

    async function createToken() {
        if (!label.trim()) { setError("กรุณาตั้งชื่อ token ก่อนสร้าง (เช่น 'คนขับ - เครื่องคลัง')"); return; }
        if (!role) { setError("กรุณาเลือก role (บรรจุ / ส่งเงิน / ขาย)"); return; }
        if (role === "SALES" && !routeUid) { setError("บัตรขายต้องเลือกสาย"); return; }
        setLoading(true);
        setError("");
        try {
            const res = await api("POST", "", role === "SALES"
                ? { label: label.trim(), role, routeUid }
                : { label: label.trim(), role });
            setNewToken(res);
            setLabel("");
            setRole("");
            setRouteUid("");
            await load();
        } catch (e) {
            setError(e.message || "สร้างไม่สำเร็จ");
        } finally {
            setLoading(false);
        }
    }

    async function revokeToken(id) {
        if (!window.confirm("ยืนยัน revoke token นี้? แท็บเล็ตเครื่องนี้จะใช้งานต่อไม่ได้ทันที")) return;
        try {
            await api("POST", `/${id}/revoke`);
            await load();
        } catch (e) {
            setError(e.message || "revoke ไม่สำเร็จ");
        }
    }

    const fetchView = async (id) => {
        setViewData(prev => ({ ...prev, [id]: { loading: true } }));
        try {
            const data = await api("GET", `/${id}/view`);
            setViewData(prev => ({ ...prev, [id]: { loading: false, token: data.token } }));
        } catch (e) {
            if (e.status === 410) {
                setViewData(prev => ({ ...prev, [id]: { loading: false, gone: true } }));
            } else {
                setViewData(prev => ({ ...prev, [id]: { loading: false, error: e.message } }));
            }
        }
    };

    const toggleView = (id) => {
        if (viewOpenId === id) { setViewOpenId(null); return; }
        setViewOpenId(id);
        if (!viewData[id]) fetchView(id);
    };

    const filteredTokens = tokens.filter(t => tokenSubTab === "Active" ? !t.revoked : t.revoked);

    return (
        <div style={{ maxWidth: "900px" }}>
            <h3>Tablet Tokens — แอป IMD (บรรจุ/ส่งเงิน)</h3>
            <p style={{ color: "#666", fontSize: "13px" }}>
                สร้าง token ต่อ role (บรรจุ/ส่งเงิน) — plaintext จะแสดง <b>ครั้งเดียว</b> ตอนสร้าง (ดูซ้ำได้ทีหลังผ่านปุ่ม <b>View</b>)
            </p>

            <div style={{ display: "flex", gap: "8px", marginBottom: "16px", alignItems: "center", flexWrap: "wrap" }}>
                <input
                    type="text"
                    placeholder="ชื่อ token เช่น 'คนขับ - เครื่องคลัง'"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    style={{ padding: "8px", flex: 1, minWidth: "220px" }}
                />
                <select
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    style={{ padding: "8px", minWidth: "160px" }}
                >
                    <option value="">-- เลือก Role --</option>
                    {ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                {role === "SALES" && (
                    <select
                        value={routeUid}
                        onChange={e => setRouteUid(e.target.value)}
                        style={{ padding: "8px", minWidth: "200px" }}
                    >
                        <option value="">-- เลือกสาย --</option>
                        {routes.map(r => (
                            <option key={r.uid} value={r.uid}>{r.nameuser}{r.username && r.username !== r.uid ? ` (${r.username})` : ""}</option>
                        ))}
                    </select>
                )}
                <button onClick={createToken} disabled={loading}
                    style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                    {loading ? "กำลังสร้าง..." : "+ สร้าง Token"}
                </button>
            </div>

            {error && <div style={{ color: "#c00", marginBottom: "10px" }}>⚠️ {error}</div>}

            {newToken && (
                <div style={{ background: "#fff8e1", border: "1px solid #ffd54f", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
                    <b>🔑 Token ใหม่สำหรับ "{newToken.label}" ({ROLES.find(r => r.value === newToken.role)?.label || newToken.role}) — สแกน QR หรือ copy ไปตอน login</b>
                    <p style={{ color: "#666", fontSize: "13px", marginTop: "4px" }}>
                        ดูซ้ำได้ทีหลังผ่านปุ่ม View ในตารางด้านล่าง ไม่จำเป็นต้องรีบบันทึกตอนนี้
                    </p>
                    <div style={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "8px", flexWrap: "wrap" }}>
                        <div style={{ backgroundColor: "#fff", padding: "12px", display: "inline-block" }}>
                            <QRCodeCanvas
                                value={newToken.token}
                                size={180}
                                bgColor="#FFFFFF"
                                fgColor="#000000"
                                marginSize={4}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: "220px" }}>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <code style={{ flex: 1, wordBreak: "break-all", background: "#fff", padding: "8px", border: "1px solid #ddd" }}>
                                    {newToken.token}
                                </code>
                                <button onClick={() => navigator.clipboard.writeText(newToken.token)}
                                    style={{ padding: "8px 12px", cursor: "pointer" }}>Copy</button>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: "10px" }}>
                        <button onClick={() => setNewToken(null)}
                            style={{ padding: "6px 14px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                            ปิด
                        </button>
                    </div>
                </div>
            )}

            <div style={{ display: "flex", borderBottom: "1px solid #ddd", marginBottom: "10px" }}>
                {["Active", "Revoked"].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setTokenSubTab(tab)}
                        style={{
                            padding: "6px 14px",
                            cursor: "pointer",
                            backgroundColor: tokenSubTab === tab ? "#007bff" : "#f8f9fa",
                            color: tokenSubTab === tab ? "white" : "black",
                            border: "none",
                            borderBottom: tokenSubTab === tab ? "2px solid #0056b3" : "none",
                            marginRight: "5px",
                            borderRadius: "4px 4px 0 0",
                            fontWeight: "bold",
                            fontSize: "13px"
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr style={{ background: "#f8f9fa", textAlign: "left" }}>
                        <th style={{ padding: "8px" }}>ชื่อ token</th>
                        <th style={{ padding: "8px" }}>Role</th>
                        <th style={{ padding: "8px" }}>สาย</th>
                        <th style={{ padding: "8px" }}>สร้างเมื่อ</th>
                        <th style={{ padding: "8px" }}>ใช้ล่าสุด</th>
                        <th style={{ padding: "8px" }}>สถานะ</th>
                        <th style={{ padding: "8px" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredTokens.map(t => (
                        <React.Fragment key={t.id}>
                            <tr style={{ borderBottom: "1px solid #eee" }}>
                                <td style={{ padding: "8px" }}>{t.label || "-"}</td>
                                <td style={{ padding: "8px" }}>{ROLES.find(r => r.value === t.role)?.label || t.role}</td>
                                <td style={{ padding: "8px" }}>{t.routeUid ? routeName(t.routeUid) : ""}</td>
                                <td style={{ padding: "8px" }}>{t.createdAt ? new Date(t.createdAt).toLocaleString("th-TH") : ""}</td>
                                <td style={{ padding: "8px" }}>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString("th-TH") : (t.routeUid ? "ยังไม่เคยใช้" : "")}</td>
                                <td style={{ padding: "8px" }}>{t.revoked ? "🚫 revoked" : "✅ active"}</td>
                                <td style={{ padding: "8px" }}>
                                    <div style={{ display: "flex", gap: "6px" }}>
                                        {tokenSubTab === "Active" && (
                                            <button onClick={() => toggleView(t.id)}
                                                style={{ padding: "4px 10px", background: "#17a2b8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                                                {viewOpenId === t.id ? "Hide" : "View"}
                                            </button>
                                        )}
                                        {!t.revoked && (
                                            <button onClick={() => revokeToken(t.id)}
                                                style={{ padding: "4px 10px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            {viewOpenId === t.id && (
                                <tr style={{ borderBottom: "1px solid #eee", background: "#f8f9fa" }}>
                                    <td colSpan="7" style={{ padding: "8px 8px 8px 24px" }}>
                                        {viewData[t.id]?.loading && <span style={{ color: "#666" }}>กำลังโหลด...</span>}
                                        {viewData[t.id]?.gone && (
                                            <span style={{ color: "#c00" }}>ไม่สามารถดู token นี้ได้ (สร้างก่อนรองรับฟีเจอร์นี้)</span>
                                        )}
                                        {viewData[t.id]?.error && (
                                            <span style={{ color: "#c00" }}>โหลด token ไม่สำเร็จ: {viewData[t.id].error}</span>
                                        )}
                                        {viewData[t.id]?.token && (
                                            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                                                <QRCodeCanvas
                                                    value={viewData[t.id].token}
                                                    size={140}
                                                    bgColor="#FFFFFF"
                                                    fgColor="#000000"
                                                    marginSize={3}
                                                />
                                                <div style={{ display: "flex", gap: "8px", alignItems: "center", flex: 1, minWidth: "220px" }}>
                                                    <code style={{ flex: 1, wordBreak: "break-all", background: "#fff", padding: "8px", border: "1px solid #ddd" }}>
                                                        {viewData[t.id].token}
                                                    </code>
                                                    <button onClick={() => navigator.clipboard.writeText(viewData[t.id].token)}
                                                        style={{ padding: "6px 12px", cursor: "pointer" }}>Copy</button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                    {filteredTokens.length === 0 && !error && (
                        <tr><td colSpan="7" style={{ padding: "12px", color: "#999" }}>
                            {tokenSubTab === "Active" ? "ยังไม่มี token ที่ active" : "ยังไม่มี token ที่ถูก revoke"}
                        </td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
